import React, { useState, useEffect } from 'react';
import { Modal, Button, message, Tabs, Typography, Space, Alert } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { getWidgetEmbedCode, WidgetEmbedCode } from '../../lib/widgetsApi';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import html from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import markdown from 'react-syntax-highlighter/dist/esm/languages/hljs/markdown';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';

SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('html', html);
SyntaxHighlighter.registerLanguage('markdown', markdown);

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface WidgetEmbedModalProps {
  visible: boolean;
  widgetId: string;
  onClose: () => void;
}

const WidgetEmbedModal: React.FC<WidgetEmbedModalProps> = ({
  visible,
  widgetId,
  onClose,
}) => {
  const [embedCode, setEmbedCode] = useState<WidgetEmbedCode | null>(null);
  const [loading, setLoading] = useState(false);
  const [copiedScript, setCopiedScript] = useState(false);

  useEffect(() => {
    if (visible && widgetId) {
      loadEmbedCode();
    }
  }, [visible, widgetId]);

  const loadEmbedCode = async () => {
    setLoading(true);
    try {
      const data = await getWidgetEmbedCode(widgetId);
      setEmbedCode(data);
    } catch (error: any) {
      message.error('Failed to load embed code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyScript = () => {
    if (embedCode) {
      navigator.clipboard.writeText(embedCode.scriptCode);
      setCopiedScript(true);
      message.success('Script code copied to clipboard');
      setTimeout(() => setCopiedScript(false), 2000);
    }
  };

  const handleCopyWidgetKey = () => {
    if (embedCode) {
      navigator.clipboard.writeText(embedCode.widgetKey);
      message.success('Widget key copied to clipboard');
    }
  };

  return (
    <Modal
      title="Embed Chat Widget"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={900}
    >
      {loading ? (
        <div className="text-center py-8">Loading embed code...</div>
      ) : embedCode ? (
        <div>
          <Alert
            message="Installation Instructions"
            description="Copy and paste the script code below into your website, just before the closing </body> tag."
            type="info"
            showIcon
            className="mb-4"
          />

          <Tabs defaultActiveKey="script">
            <TabPane tab="Script Code" key="script">
              <div className="mb-4">
                <Space className="mb-2">
                  <Text strong>Step 1:</Text>
                  <Text>Copy the script code below</Text>
                </Space>
                <div className="relative">
                  <SyntaxHighlighter
                    language="html"
                    style={docco}
                    customStyle={{
                      padding: '16px',
                      borderRadius: '4px',
                      fontSize: '13px',
                    }}
                  >
                    {embedCode.scriptCode}
                  </SyntaxHighlighter>
                  <Button
                    type="primary"
                    icon={copiedScript ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={handleCopyScript}
                    className="absolute top-2 right-2"
                  >
                    {copiedScript ? 'Copied!' : 'Copy Script'}
                  </Button>
                </div>
              </div>

              <div>
                <Space className="mb-2">
                  <Text strong>Step 2:</Text>
                  <Text>Paste it before the closing {`</body>`} tag of your website</Text>
                </Space>
                <Paragraph type="secondary" className="text-sm">
                  The widget will automatically load and position itself according to your
                  configuration. Make sure to test it on your development environment first.
                </Paragraph>
              </div>
            </TabPane>

            <TabPane tab="Widget Key" key="key">
              <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>Widget Key:</Text>
                    <div className="mt-2 p-4 bg-gray-50 rounded flex justify-between items-center">
                      <code className="text-lg">{embedCode.widgetKey}</code>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyWidgetKey}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <Alert
                    message="Keep your widget key secure"
                    description="This key is used to identify your widget. Don't share it publicly if you've configured domain restrictions."
                    type="warning"
                    showIcon
                    className="mt-4"
                  />
                </Space>
              </div>
            </TabPane>

            <TabPane tab="Documentation" key="docs">
              <div className="prose prose-sm max-w-none">
                <SyntaxHighlighter
                  language="markdown"
                  style={docco}
                  customStyle={{
                    padding: '16px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    background: '#f9fafb',
                  }}
                >
                  {embedCode.instructions}
                </SyntaxHighlighter>
              </div>
            </TabPane>

            <TabPane tab="HTML Example" key="html">
              <div>
                <Paragraph>
                  Here's a complete HTML example showing how to embed the widget:
                </Paragraph>
                <div className="relative">
                  <SyntaxHighlighter
                    language="html"
                    style={docco}
                    customStyle={{
                      padding: '16px',
                      borderRadius: '4px',
                      fontSize: '13px',
                    }}
                  >
                    {`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>My Website</title>
</head>
<body>
  <h1>Welcome to My Website</h1>
  <p>This is my website content...</p>

  ${embedCode.scriptCode}
</body>
</html>`}
                  </SyntaxHighlighter>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          No embed code available
        </div>
      )}
    </Modal>
  );
};

export default WidgetEmbedModal;
